import "./SearchPage.scss";
import {
  GridRow,
  HeroBasic,
  ListSearch,
  GridPresetList,
  Pagination,
  LoadingOverlay,
  SortByRadioButton,
  SearchGlobalField,
  RadioButton,
  GridPresetSidebar,
  HideFor,
  Expander,
  SearchPageTipsAndFuzzy,
} from "/App.Components";
import { useEffect, useRef, useState } from "react";
import { SearchPageProps } from "/Models/Generated/SearchPageProps";
import SearchClient from "./SearchClient";
import useAutocompleteSuggestions from "/Helpers/Hooks/useAutocompleteSuggestions";
import useScrollTopTracker from "/Helpers/Hooks/useScrollTopTracker";
import SearchPageFilters from "./SearchPageFilters";

const SearchPage = ({
  dateLabel,
  noResultsText,
  pageHeader,
  relevanceLabel,
  searchResultFormatString,
  searchResults,
  sortByLabel,
  pagination,
  searchSettings,
  enableFuzzySearch,
  categoryDropdown,
  yearDropdown,
  fuzzySelected,
  fuzzySearchLabel,
  filterSelectedAriaLiveSuffix,
  categoriesSelected,
  yearSelected,
  clearAllFiltersButtonText,
  filtersHeading,
  searchTipsLabel,
  searchTips,
  showFiltersAriaLabel = "Vis filtre",
  hideFiltersAriaLabel = "Skjul filtre",
}: SearchPageProps) => {
  const [results, setResults] = useState(searchResults);
  const [pageNumber, setPageNumber] = useState(searchResults?.pageNumber ?? 1);
  const [totalPages, setTotalPages] = useState(searchResults?.totalPages ?? 0);
  const autocomplete = useAutocompleteSuggestions(
    searchSettings?.endpointAutocomplete,
    searchSettings?.language
  );
  const [fuzzy, setFuzzy] = useState(fuzzySelected);
  const [year, setYear] = useState(
    yearDropdown?.items?.filter((i) => i.value === yearSelected + "")[0]
  );
  const [category, setCategory] = useState(
    categoryDropdown?.items?.filter(
      (i) => i.value === categoriesSelected + ""
    )[0]
  );
  const [searchTerm, setSearchTerm] = useState(searchResults?.searchTerm ?? "");
  const [loading, setLoading] = useState(false);
  const [sortByRelevance, setSortByRelevance] = useState(
    searchResults?.sortByRelevance ?? true
  );
  const [shouldPushState, setShouldPushState] = useState(true);
  const numbers = [];
  const scrollToRef = useRef<HTMLDivElement>(null);
  for (let i = 1; i <= (totalPages ?? 0); ++i) numbers.push(i);

  const [doScroll, setDoScroll] = useState(false);

  const scrollTop = useScrollTopTracker(scrollToRef);

  useEffect(() => {
    if (loading) return;
    if (!doScroll) return;
    setDoScroll(false);
    window.scrollTo({
      top: scrollTop,
      behavior: "smooth",
    });
  }, [loading]);

  const updateList = () => {
    try {
      const client = new SearchClient(
        searchSettings?.endpointSearch ?? "",
        searchSettings?.language ?? "",
        searchSettings?.searchPageId ?? 0
      );
      const yearValue = Number.isInteger(Number(year?.value))
        ? Number(year?.value)
        : undefined;
      client
        .GetItems(
          pageNumber,
          searchTerm,
          sortByRelevance,
          fuzzy,
          yearValue,
          category?.value
        )
        .then((results) => {
          setLoading(false);
          setResults(results);
          setTotalPages(results.totalPages);
          if (shouldPushState) {
            const newUrl = client.GetUrl(
              pageNumber,
              searchTerm,
              sortByRelevance,
              fuzzy,
              yearValue,
              category?.value
            );
            window.history.pushState(null, "", newUrl);
          }
          setShouldPushState(true);
        });
    } catch {}
  };

  useEffect(() => {
    if (loading) updateList();
  }, [loading]);

  useEffect(() => {
    const handlePopState = () => {
      const client = new SearchClient(
        searchSettings?.endpointSearch ?? "",
        searchSettings?.language ?? "",
        searchSettings?.searchPageId ?? 0
      );
      setSearchTerm(client.GetTermFromUrl());
      setPageNumber(client.GetPageNumberFromUrl());
      setSortByRelevance(client.GetSortByRelevanceFromUrl());
      setShouldPushState(false);
      setLoading(true);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);
  return (
    <div className="search-page-norad">
      <LoadingOverlay loading={loading} />
      {pageHeader && <HeroBasic {...pageHeader} />}
      <div className="search-page-norad__search-container">
        <GridRow>
          <GridPresetList>
            <SearchGlobalField
              noBottomPadding={
                enableFuzzySearch || !!(searchTips && searchTipsLabel)
              }
              noTopPadding
              tabIndex={0}
              id="searchfield-searchpage"
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              autocompleteItems={autocomplete.items}
              setAutocompleteItems={autocomplete.setItems}
              searchButtonAriaLabel={
                searchSettings?.searchButtonAriaLabel ?? ""
              }
              searchPlaceholder={searchSettings?.searchPlaceholder ?? ""}
              doAutocomplete={autocomplete.fetchSuggestions}
              doSearch={() => {
                setPageNumber(1);
                setLoading(true);
              }}
            />
            <SearchPageTipsAndFuzzy
              {...{
                fuzzy,
                setFuzzy,
                searchTips,
                searchTipsLabel,
                enableFuzzySearch,
                fuzzySearchLabel,
                setPageNumber,
                setLoading,
              }}
            />
          </GridPresetList>
        </GridRow>
      </div>
      <GridRow>
        <GridPresetList>
          <div className="search-page-norad__result-count" ref={scrollToRef}>
            {`${searchResultFormatString?.replace(
              "_",
              (results?.totalItems ?? 0) + ""
            )}`}
          </div>
          {(categoryDropdown || yearDropdown) && (
            <HideFor desktop desktopMedium desktopLargeUp>
              <div className="search-page-norad__expander-container">
                <Expander
                  heading={filtersHeading || "Filtre"}
                  ariaLabelCollapse={hideFiltersAriaLabel}
                  ariaLabelExpand={showFiltersAriaLabel}
                >
                  <SearchPageFilters
                    {...{
                      clearAllFiltersButtonText,
                      setPageNumber,
                      setLoading,
                      setYear,
                      year,
                      yearDropdown,
                      setCategory,
                      category,
                      categoryDropdown,
                      filterSelectedAriaLiveSuffix,
                    }}
                  />
                </Expander>
              </div>
            </HideFor>
          )}
          <div className="search-page-norad__sort-by-container">
            <SortByRadioButton description={sortByLabel}>
              <RadioButton
                name="sortby"
                disabled={loading}
                checked={sortByRelevance}
                label={relevanceLabel ?? ""}
                onClick={() => {
                  if (sortByRelevance) return;
                  setSortByRelevance(true);
                  setPageNumber(1);
                  setLoading(true);
                }}
              />
              <RadioButton
                name="sortby"
                disabled={loading}
                checked={!sortByRelevance}
                label={dateLabel ?? ""}
                onClick={() => {
                  if (!sortByRelevance) return;
                  setSortByRelevance(false);
                  setPageNumber(1);
                  setLoading(true);
                }}
              />
            </SortByRadioButton>
          </div>
        </GridPresetList>
        {(categoryDropdown || yearDropdown) && (
          <GridPresetSidebar gridRow={2} hideForMobileAndTablet>
            <HideFor mobile tablet>
              <SearchPageFilters
                {...{
                  filtersHeading,
                  clearAllFiltersButtonText,
                  setPageNumber,
                  setLoading,
                  setYear,
                  year,
                  yearDropdown,
                  setCategory,
                  category,
                  categoryDropdown,
                  filterSelectedAriaLiveSuffix,
                }}
              />
            </HideFor>
          </GridPresetSidebar>
        )}
        <GridPresetList>
          <div className="search-page-norad__results-section">
            {!loading && !results?.items?.length && (
              <div className="search-page-norad__no-results">
                {noResultsText}
              </div>
            )}
            {!loading && (results?.items?.length ?? 0) > 0 && (
              <ListSearch items={results?.items} />
            )}
            {!loading && (results?.totalPages ?? 0) > 1 && (
              <div className="search-page-norad__pagination-container">
                <Pagination
                  {...pagination}
                  currentPage={pageNumber}
                  setCurrentPage={(page: number) => {
                    setPageNumber(page);
                    setLoading(true);
                    setDoScroll(true);
                  }}
                  totalPages={totalPages}
                />
              </div>
            )}
          </div>
        </GridPresetList>
      </GridRow>
    </div>
  );
};

export default SearchPage;
